"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Incident = void 0;
const tslib_1 = require("tslib");
const config_1 = tslib_1.__importDefault(require("@iotv/config"));
const constants_1 = require("../../constants");
const identity_1 = require("../../util/objects/identity");
const millisecondOperations_1 = require("../../util/time/millisecondOperations");
const ModelCommon_1 = require("../ModelCommon");
const enums_1 = require("./enums");
const IncidentTypes_1 = require("./IncidentTypes");
const debug = !process.env.AWS_SESSION_TOKEN && false;
const typeName = 'Incident';
class Incident extends ModelCommon_1.ModelCommon {
    constructor({ _status, _severity, ...ob }) {
        super(ob.type || typeName, { ...ob });
        this._status = _status;
        this._severity = _severity;
        this.updateDynamicKeys();
    }
    get summary() {
        return this._summary;
    }
    set summary(summary) {
        this._summary = summary;
    }
    addToHistory(historyRecord) {
        if (!this.history) {
            this.history = [];
        }
        this.history.unshift(historyRecord);
        let i = this.history?.length ?? 0;
        //purge when lots of events
        while (this.history?.length > 25) {
            const idx = i - 1;
            const historyEvent = this.history[i] && this.history[i].event;
            if (!historyEvent || ![IncidentTypes_1.IncidentEvent.OPENED, IncidentTypes_1.IncidentEvent.CLOSED].includes(historyEvent)) {
                this.history.pop();
            }
            i--;
        }
    }
    addNotificationRecord(notificationRecord) {
        if (!this.notificationRecords) {
            this.notificationRecords = {};
        }
        const acceptableDynamoDBKey = (0, identity_1.getAcceptableDynamoDBKey)(notificationRecord.recipient.sk);
        if (!this.notificationRecords[acceptableDynamoDBKey]) {
            this.notificationRecords[acceptableDynamoDBKey] = [];
        }
        this.notificationRecords[acceptableDynamoDBKey].push(notificationRecord);
    }
    openIncident(initiator = constants_1.SYSTEM) {
        debug && console.log('Setting status open');
        this.status = IncidentTypes_1.IncidentStatus.OPEN;
        debug && console.log('status', this.status);
        const historyRecord = {
            timestamp: Date.now(),
            initiator,
            event: IncidentTypes_1.IncidentEvent.OPENED,
        };
        this.addToHistory(historyRecord);
        this.updateDynamicKeys();
    }
    closeIncident(initiator) {
        this.status = IncidentTypes_1.IncidentStatus.CLOSED;
        const historyRecord = {
            timestamp: Date.now(),
            initiator,
            event: IncidentTypes_1.IncidentEvent.CLOSED,
        };
        this.addToHistory(historyRecord);
        this.updateDynamicKeys();
    }
    // returns ghostified Incident for use when need correct dsk dpk of an incident for an alert trigger
    getIncidentZombieForAlert(alertTrigger) {
        let derivedIncident = undefined;
        const incidentEntity = alertTrigger.entityTriggeringAlert;
        const incidentType = alertTrigger.alertTriggerType && (enums_1.AlertIncidentType[alertTrigger.alertTriggerType] ?? enums_1.AlertIncidentType.SYSTEM_ISSUE);
        const { initiator, severity, status, summary, alertTriggerSubtype: incidentSubtype } = alertTrigger;
        if (initiator && incidentEntity && incidentType) {
            debug && console.log('getting dsk from initiator, severity, status, summary, alertTriggerSubtype, on alertTrigger ', alertTrigger);
            const derivedIncidentId = (0, IncidentTypes_1.getIncidentDSK)({
                initiator,
                incidentEntity,
                incidentType,
                incidentSubtype,
            });
            debug && console.log('derived dsk is ', derivedIncidentId);
            derivedIncident = {
                sk: config_1.default.app.ghostUuid,
                pk: incidentEntity.sk,
                dsk: derivedIncidentId,
                dpk: typeName,
                type: typeName,
                id: derivedIncidentId,
                _initiator: initiator,
                _incidentEntity: incidentEntity,
                _incidentType: incidentType,
                _incidentSubtype: incidentSubtype,
                _severity: severity,
                _status: status,
                _summary: summary,
                closedTimestamp: undefined,
                timestamp: undefined,
                ttl: undefined,
            };
        }
        else {
            debug && console.log('No initiator, severity, status, on alertTrigger', alertTrigger);
        }
        debug && console.log('returning derivedIncident', derivedIncident);
        return derivedIncident;
    }
    recordAlertTrigger(alertTrigger) {
        const historyRecord = {
            initiator: alertTrigger.initiator ?? constants_1.SYSTEM,
            timestamp: Date.now(),
            event: alertTrigger.alertTriggerType ?? IncidentTypes_1.IncidentEvent.UPDATED,
            summary: alertTrigger.summary,
            alertSk: alertTrigger.sk,
        };
        this.addToHistory(historyRecord);
        this.updateDynamicKeys();
    }
    updateDynamicKeys() {
        const { incidentEntity, incidentType, incidentSubtype, initiator } = this;
        if (incidentEntity && incidentType && initiator) {
            this.dsk = (0, IncidentTypes_1.getIncidentDSK)({
                incidentEntity,
                incidentType,
                incidentSubtype,
                initiator,
            });
        }
        else {
            debug &&
                console.log('WARN updateDynamicKeys something missing', {
                    incidentEntity,
                    incidentType,
                    initiator,
                });
        }
        this.dpk = this.type;
    }
    get incidentEntity() {
        return this._incidentEntity;
    }
    set incidentEntity(entity) {
        this._incidentEntity = entity;
    }
    get incidentEntityName() {
        return this._incidentEntity?.name;
    }
    set incidentEntityName(name) {
        if (this._incidentEntity)
            this._incidentEntity.name = name;
    }
    get status() {
        return this._status;
    }
    set status(newStatus) {
        this._status = newStatus;
        this.updateDynamicKeys();
    }
    get severity() {
        return this._severity;
    }
    set severity(newSeverity) {
        this._severity = newSeverity;
        this.updateDynamicKeys();
    }
    get initiator() {
        return this._initiator;
    }
    set initiator(initiator) {
        this._initiator = initiator;
    }
    get incidentType() {
        return this._incidentType;
    }
    set incidentType(incidentType) {
        this._incidentType = incidentType;
    }
    get incidentSubtype() {
        return this._incidentSubtype;
    }
    set incidentSubtype(incidentSubtype) {
        this._incidentSubtype = incidentSubtype;
    }
    /** timestamp is string in dynamodb main-table */
    get timestampOpen() {
        return this.timestamp ?? undefined;
    }
    set timestampOpen(timestamp) {
        this.timestamp = timestamp;
    }
    getUserAttributes() {
        const atts = {
            ...super.getUserAttributes(),
            _incidentType: {
                type: 'String',
                label: 'Incident Type',
                editable: false,
                displayFn: (v) => enums_1.IncidentTypeNames[v],
            },
            _severity: {
                type: 'String',
                label: 'Severity',
                editable: false,
                displayFn: (v) => v.slice(0, 1) + v.slice(1).toLowerCase().replaceAll('_', ' '),
            },
            _status: {
                type: 'String',
                label: 'Status',
                editable: false,
                displayFn: (v) => v.slice(0, 1) + v.slice(1).toLowerCase().replaceAll('_', ' '),
            },
            timestamp: { type: 'Number', editable: false, displayFn: (v) => (0, millisecondOperations_1.getNiceDate)(v) },
        };
        delete atts.description;
        delete atts.type;
        if (this.status === IncidentTypes_1.IncidentStatus.CLOSED) {
            atts.closedTimestamp = {
                type: 'Date',
                label: 'Closed at',
                editable: false,
            };
        }
        return atts;
    }
}
exports.Incident = Incident;
